/**
 * holidays.js
 * -----------
 * Returns a Map of  "YYYY-MM-DD" → string[]  for a given year.
 * Call  getHolidays(year)  and look up any date to get its label(s).
 *
 * Holiday rule types covered:
 *   1. Fixed date         – New Year's, Valentine's, Christmas, etc.
 *   2. Nth weekday        – MLK Day, Thanksgiving, Mother's Day, etc.
 *   3. Easter-based       – Easter, Good Friday, Ash Wednesday, Mardi Gras, Pentecost
 *   4. DST transitions    – US Daylight Saving start/end
 *   5. Astronomical       – Vernal/Autumnal equinox, June/December solstice (UTC)
 *   6. Jewish calendar    – Passover, Rosh Hashanah, Yom Kippur, Hanukkah
 *   7. Lunar / cultural   – Chinese New Year, Ramadan (1st day)
 *
 * Lunar/Jewish dates use pre-computed lookup tables for 2024-2040.
 * Extend the tables for other years as needed.
 */

// ─── helpers ─────────────────────────────────────────────────────────────────

/** Format a Date as "YYYY-MM-DD" (local time). */
function fmt(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Create a local Date for year/month(1-based)/day. */
function date(y, m, d) {
  return new Date(y, m - 1, d);
}

/**
 * Return the date of the Nth occurrence of a weekday in a month.
 * @param {number} year
 * @param {number} month  1-based
 * @param {number} weekday  0=Sun … 6=Sat
 * @param {number} n  1-based; negative counts from the end (-1 = last)
 */
function nthWeekday(year, month, weekday, n) {
  if (n > 0) {
    const first = date(year, month, 1);
    const offset = (weekday - first.getDay() + 7) % 7;
    return date(year, month, 1 + offset + (n - 1) * 7);
  } else {
    // count from end of month
    const last = new Date(year, month, 0); // last day of month
    const offset = (last.getDay() - weekday + 7) % 7;
    const abs = Math.abs(n);
    return new Date(last.getFullYear(), last.getMonth(), last.getDate() - offset - (abs - 1) * 7);
  }
}

// ─── 1. Fixed-date holidays ───────────────────────────────────────────────────

function fixedHolidays(year, map) {
  const add = (m, d, name) => push(map, fmt(date(year, m, d)), name);

  add(1,  1,  "New Year's Day");
  add(2,  2,  "Groundhog Day");
  add(2,  14, "Valentine's Day");
  add(3,  17, "St. Patrick's Day");
  add(4,  1,  "April Fool's Day");
  add(4,  22, "Earth Day");
  add(5,  5,  "Cinco de Mayo");
  add(6,  14, "Flag Day");
  add(6,  19, "Juneteenth");
  add(7,  4,  "Independence Day");
  add(8,  19, "Aviation Day");
  add(9,  11, "Patriot Day");
  add(9,  17, "Constitution Day");
  add(10, 16, "Boss's Day");
  add(10, 24, "United Nations Day");
  add(11, 11, "Veterans Day");
  add(12, 7,  "Pearl Harbor Remembrance Day");
  add(12, 24, "Christmas Eve");
  add(12, 25, "Christmas Day");
  add(12, 31, "New Year's Eve");
}

// ─── 2. Nth-weekday holidays ──────────────────────────────────────────────────

function nthWeekdayHolidays(year, map) {
  const add = (m, wd, n, name) => push(map, fmt(nthWeekday(year, m, wd, n)), name);

  // January
  add(1, 1, 3, "MLK Day");                    // 3rd Monday of January

  // February
  add(2, 1, 3, "Presidents' Day");             // 3rd Monday of February

  // March
  add(3, 0, 2, "Daylight Saving");             // 2nd Sunday of March

  // May
  add(5, 0, 2, "Mother's Day");                // 2nd Sunday of May
  add(5, 6, 3, "Armed Forces Day");            // 3rd Saturday of May
  add(5, 1, -1, "Memorial Day");               // Last Monday of May

  // June
  add(6, 0, 3, "Father's Day");               // 3rd Sunday of June

  // July
  add(7, 0, 4, "Parents' Day");               // 4th Sunday of July

  // September
  add(9, 0, 1, "Grandparents Day");           // 1st Sunday of September
  add(9, 1, 1, "Labor Day");                  // 1st Monday of September

  // October
  add(10, 1, 2, "Columbus Day");              // 2nd Monday of October
  add(10, 6, 3, "Sweetest Day");              // 3rd Saturday of October

  // November
  add(11, 0, 1, "Daylight Saving"); // 1st Sunday of November
  add(11, 4, 4, "Thanksgiving");              // 4th Thursday of November

  // April – Administrative Professionals' Day (Wed of last full week of April)
  // "Last full week" = Mon-Sun week entirely in April; take the Wednesday.
  // Equivalent: last Wednesday of April whose Monday is also in April.
  let adminWed = nthWeekday(year, 4, 3, -1); // last Wednesday of April
  // Ensure the Monday of that week is still in April
  const monOfThatWeek = new Date(adminWed);
  monOfThatWeek.setDate(adminWed.getDate() - 2);
  if (monOfThatWeek.getMonth() !== 3) {
    // step back one week
    adminWed = new Date(adminWed);
    adminWed.setDate(adminWed.getDate() - 7);
  }
  push(map, fmt(adminWed), "Administrative Professionals' Day");
}

// ─── 3. Easter and Easter-based holidays ─────────────────────────────────────
// Anonymous Gregorian algorithm (Meeus / Jones / Butcher)

function easterDate(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return date(year, month, day);
}

function easterBasedHolidays(year, map) {
  const easter = easterDate(year);
  const offset = (days) => {
    const d = new Date(easter);
    d.setDate(d.getDate() + days);
    return d;
  };

  push(map, fmt(offset(-47)), "Mardi Gras");
  push(map, fmt(offset(-46)), "Ash Wednesday");
  push(map, fmt(offset(-2)),  "Good Friday");
  push(map, fmt(easter),      "Easter Sunday");
  push(map, fmt(offset(49)),  "Pentecost Sunday");
}

// ─── 4. Astronomical events ───────────────────────────────────────────────────
// Approximate UTC dates via Meeus "Astronomical Algorithms" Ch. 27 linear fit.
// Accurate to ±1 day for 2020-2040.

function astronomicalEvents(year, map) {
  // JDE of mean equinox/solstice (Meeus table 27.a for years 1000-3000)
  function meeus(Y, JDE0, M, S, T2, T3) {
    const JDE = JDE0 + M * Y + S * Y * Y + T2 * Y * Y * Y + T3 * Y * Y * Y * Y;
    // Convert Julian Day to calendar date
    const jd = JDE + 0.5;
    const z = Math.floor(jd);
    const a = z < 2299161 ? z : (function () {
      const alpha = Math.floor((z - 1867216.25) / 36524.25);
      return z + 1 + alpha - Math.floor(alpha / 4);
    })();
    const b = a + 1524;
    const c = Math.floor((b - 122.1) / 365.25);
    const dd = Math.floor(365.25 * c);
    const e = Math.floor((b - dd) / 30.6001);
    const day = b - dd - Math.floor(30.6001 * e);
    const month = e < 14 ? e - 1 : e - 13;
    const year2 = month > 2 ? c - 4716 : c - 4715;
    return date(year2, month, day);
  }

  // Simplified: use known approximate offsets from J2000
  const Y = (year - 2000) / 1000;

  const vernal = meeus(Y, 2451623.80984, 365242.37404, 0.05169, -0.00411, -0.00057);
  const june   = meeus(Y, 2451716.56767, 365241.62603, 0.00325,  0.00888, -0.00030);
  const autumn = meeus(Y, 2451810.21715, 365242.01767, -0.11575, 0.00337,  0.00078);
  const dec    = meeus(Y, 2451900.05952, 365242.74049, -0.06223, -0.00823, 0.00032);

  push(map, fmt(vernal), "Vernal Equinox (UTC)");
  push(map, fmt(june),   "June Solstice (UTC)");
  push(map, fmt(autumn), "Autumnal Equinox (UTC)");
  push(map, fmt(dec),    "December Solstice (UTC)");
}

// ─── 5. Jewish calendar ───────────────────────────────────────────────────────
// Pre-computed Gregorian dates. Extend as needed.
// Source: hebcal.com

const JEWISH_HOLIDAYS = {
  // year: { passover, roshHashanah, yomKippur, hanukkah }
  2024: { passoverEve: "2024-04-22", roshHashanah: "2024-10-02", yomKippur: "2024-10-11", hanukkah: "2024-12-25" },
  2025: { passoverEve: "2025-04-12", roshHashanah: "2025-09-22", yomKippur: "2025-10-01", hanukkah: "2025-12-14" },
  2026: { passoverEve: "2026-04-01", roshHashanah: "2026-09-11", yomKippur: "2026-09-20", hanukkah: "2026-12-04" },
  2027: { passoverEve: "2027-04-21", roshHashanah: "2027-10-01", yomKippur: "2027-10-10", hanukkah: "2027-12-24" },
  2028: { passoverEve: "2028-04-10", roshHashanah: "2028-09-20", yomKippur: "2028-09-29", hanukkah: "2028-12-12" },
  2029: { passoverEve: "2029-03-30", roshHashanah: "2029-09-09", yomKippur: "2029-09-18", hanukkah: "2029-12-01" },
  2030: { passoverEve: "2030-04-17", roshHashanah: "2030-09-27", yomKippur: "2030-10-06", hanukkah: "2030-12-20" },
  2031: { passoverEve: "2031-04-07", roshHashanah: "2031-09-17", yomKippur: "2031-09-26", hanukkah: "2031-12-09" },
  2032: { passoverEve: "2032-04-25", roshHashanah: "2032-10-04", yomKippur: "2032-10-13", hanukkah: "2032-12-27" },
  2033: { passoverEve: "2033-04-14", roshHashanah: "2033-09-23", yomKippur: "2033-10-02", hanukkah: "2033-12-16" },
  2034: { passoverEve: "2034-04-03", roshHashanah: "2034-09-12", yomKippur: "2034-09-21", hanukkah: "2034-12-05" },
  2035: { passoverEve: "2035-04-23", roshHashanah: "2035-10-02", yomKippur: "2035-10-11", hanukkah: "2035-12-25" },
  2036: { passoverEve: "2036-04-11", roshHashanah: "2036-09-20", yomKippur: "2036-09-29", hanukkah: "2036-12-13" },
  2037: { passoverEve: "2037-03-31", roshHashanah: "2037-09-09", yomKippur: "2037-09-18", hanukkah: "2037-12-02" },
  2038: { passoverEve: "2038-04-19", roshHashanah: "2038-09-29", yomKippur: "2038-10-08", hanukkah: "2038-12-21" },
  2039: { passoverEve: "2039-04-09", roshHashanah: "2039-09-19", yomKippur: "2039-09-28", hanukkah: "2039-12-11" },
  2040: { passoverEve: "2040-04-27", roshHashanah: "2040-10-06", yomKippur: "2040-10-15", hanukkah: "2040-12-29" },
};

function jewishHolidays(year, map) {
  const entry = JEWISH_HOLIDAYS[year];
  if (!entry) return; // year not in table — extend as needed

  push(map, entry.passoverEve,  "Passover (sundown)");
  push(map, entry.roshHashanah, "Rosh Hashanah");
  push(map, entry.yomKippur,    "Yom Kippur");
  push(map, entry.hanukkah,     "Hanukkah begins");
}

// ─── 6. Chinese New Year (Lunar New Year) ────────────────────────────────────

const CHINESE_NEW_YEAR = {
  2024: "2024-02-10",
  2025: "2025-01-29",
  2026: "2026-02-17",
  2027: "2027-02-06",
  2028: "2028-01-26",
  2029: "2029-02-13",
  2030: "2030-02-03",
  2031: "2031-01-23",
  2032: "2032-02-11",
  2033: "2033-01-31",
  2034: "2034-02-19",
  2035: "2035-02-08",
  2036: "2036-01-28",
  2037: "2037-02-15",
  2038: "2038-02-04",
  2039: "2039-01-24",
  2040: "2040-02-12",
};

function chineseNewYear(year, map) {
  const d = CHINESE_NEW_YEAR[year];
  if (d) push(map, d, "Chinese New Year");
}

// ─── 7. Ramadan (1st day) ─────────────────────────────────────────────────────
// Dates shift ~11 days earlier each year (Islamic lunar calendar).

const RAMADAN_START = {
  2024: "2024-03-11",
  2025: "2025-03-01",
  2026: "2026-02-18",
  2027: "2027-02-08",  // ← same as 2027 in the Vertex template (Feb 8 = Monday ✓)
  2028: "2028-01-28",
  2029: "2029-01-16",
  2030: "2030-01-05",
  2031: "2030-12-26",
  2032: "2031-12-15",
  2033: "2032-12-04",
  2034: "2033-11-24",
  2035: "2034-11-13",
  2036: "2035-11-02",
  2037: "2036-10-21",
  2038: "2037-10-10",
  2039: "2038-09-29",
  2040: "2039-09-18",
};

function ramadan(year, map) {
  const d = RAMADAN_START[year];
  if (d) push(map, d, "Ramadan (1st day)");
}

// ─── Utility ──────────────────────────────────────────────────────────────────

/** Push a holiday name onto the array for a date key, creating the key if needed. */
function push(map, key, name) {
  if (!map.has(key)) map.set(key, []);
  map.get(key).push(name);
}

// ─── Main export ──────────────────────────────────────────────────────────────

/**
 * Returns a Map<"YYYY-MM-DD", string[]> containing all holidays for the year.
 *
 * Usage:
 *   const h = getHolidays(2027);
 *   const labels = h.get("2027-11-25") ?? [];  // ["Thanksgiving"]
 */
export function getHolidays(year) {
  const map = new Map();
  fixedHolidays(year, map);
  nthWeekdayHolidays(year, map);
  easterBasedHolidays(year, map);
  astronomicalEvents(year, map);
  jewishHolidays(year, map);
  chineseNewYear(year, map);
  ramadan(year, map);
  return map;
}

/**
 * Convenience: get holidays for a single month.
 *
 * Usage:
 *   const h = getHolidaysForMonth(2027, 11);
 *   // h.get("2027-11-25") → ["Thanksgiving"]
 */
export function getHolidaysForMonth(year, month) {
  const all = getHolidays(year);
  const prefix = `${year}-${String(month).padStart(2, "0")}`;
  const filtered = new Map();
  for (const [key, names] of all) {
    if (key.startsWith(prefix)) filtered.set(key, names);
  }
  return filtered;
}
